import { Injectable } from '@angular/core';
import { CircleGeofence, GeofencesAPIDataInterface, PolygonGeofence } from '../interface/map-view.interface';

@Injectable({
  providedIn: 'root'
})
export class MapGeofenceRendererService {
  private geofencePoints: (google.maps.Circle | google.maps.Polygon)[] = [];
  private virtualWayPoints: (google.maps.Polyline | google.maps.Polygon)[] = [];
  private map: google.maps.Map | null = null;

  constructor() { }


  initialize(map: google.maps.Map): void {
    this.map = map;
  }


  createCircleForGeofence(geofence: CircleGeofence): google.maps.Circle {
    if (!this.map) {
      throw new Error('Map not initialized. Call initialize() first.');
    }
    const { centerLat, centerLng, circleRadius } = geofence;
    const circle = new google.maps.Circle({
      strokeColor: '#ED711C',
      strokeOpacity: 0.8,
      strokeWeight: 2.5,
      fillColor: ' #D66D31',
      fillOpacity: 0.21,
      map: this.map,
      center: { lat: centerLat, lng: centerLng },
      radius: circleRadius,
    });
    this.geofencePoints.push(circle);
    return circle;
  }


  createPolygonGeofence(geofence: PolygonGeofence): google.maps.Polygon {
    if (!this.map) {
      throw new Error('Map not initialized. Call initialize() first.');
    }

    const polygonCoords =  (geofence?.polygonData ?? []).map(coord => {
      if(coord){
      const [lat, lng] = coord.split(',').map(Number);
        return { lat, lng };
      }
      return null;
    }).filter(coord => coord !== null);
    
    const polygon = new google.maps.Polygon({
      paths: polygonCoords,
      strokeColor: '#ED711C', 
      strokeOpacity: 0.8,
      strokeWeight: 2.5,
      fillColor: '#D66D31', 
      fillOpacity: 0.21,
      map: this.map,
    });
    this.geofencePoints.push(polygon);
    return polygon;
  }


  plotGeofencePoints(geofencesAPIData: GeofencesAPIDataInterface): void {
    if (!this.map) {
      throw new Error('Map not initialized. Call initialize() first.');
    }

    const bounds = new google.maps.LatLngBounds();
    this.clearGeofences();
    Object.entries(geofencesAPIData || {}).forEach(([geofenceId, geofence]) => {
      if (geofence?.type === 'POLYGON') {
        this.createPolygonGeofence(geofence);
        (geofence as PolygonGeofence)?.polygonData?.forEach((coord) => {
          if(coord){
            const [lat, lng] = coord.split(',').map(Number);
            bounds.extend(new google.maps.LatLng(lat, lng));
          }
        });
      } else if (geofence?.type === 'CIRCLE') {
        this.createCircleForGeofence(geofence);
        const { centerLat, centerLng } = geofence as CircleGeofence;
        if(centerLat && centerLng){
          bounds.extend(new google.maps.LatLng(centerLat, centerLng));
        }
      }
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  private createDashedCircle(
    lat: number,
    lng: number,
    radius: number
  ): void {
    if (!this.map) {
      throw new Error('Map not initialized. Call initialize() first.');
    }

    const center = new google.maps.LatLng(lat, lng);
    const earthRadius = 6378137;
    const dashCount = 8;
    const dashAngle = 360 / dashCount;
    const gapAngle = dashAngle * 0.4; 

    const circlePath: google.maps.LatLngLiteral[] = [];
    const numSides = 45;
    
    for (let i = 0; i <= numSides; i++) {
      const angle = (i * 360 / numSides) * (Math.PI / 180);
      const pointLat = lat + (radius / earthRadius) * (180 / Math.PI) * Math.sin(angle);
      const pointLng = lng + (radius / earthRadius) * (Math.cos(angle) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180));
      circlePath.push({ lat: pointLat, lng: pointLng });
    }

    const filledCircle = new google.maps.Polygon({
      paths: circlePath,
      strokeColor: ' #AACEF857',
      strokeOpacity: 0.3,
      strokeWeight: 0,
      fillColor: "#AACEF857",
      fillOpacity: 0.7,
      map: this.map,
    });

    for (let i = 0; i < dashCount; i++) {
      const startAngle = i * dashAngle;
      const endAngle = startAngle + (dashAngle - gapAngle);
      // create an arc for each dash
      const arcPath: google.maps.LatLngLiteral[] = [];
      const steps = 5; // points in each dash
      
      for (let j = 0; j <= steps; j++) {
        const angle = startAngle + (j / steps) * (endAngle - startAngle);
        const radians = angle * Math.PI / 180;
        const dx = radius * Math.cos(radians);
        const dy = radius * Math.sin(radians);

        const latOffset = (dy / earthRadius) * (180 / Math.PI);
        const lngOffset = (dx / earthRadius) * (180 / Math.PI) / Math.cos((Math.PI / 180) * lat);
        arcPath.push({
          lat: lat + latOffset,
          lng: lng + lngOffset
        });
      }
      const dashedPolyline = new google.maps.Polyline({
        path: arcPath,
        geodesic: true,
        strokeColor:  '#0937B2',
        strokeOpacity:  0.5,
        strokeWeight: 2,
        map: this.map,
      });
      this.virtualWayPoints.push(dashedPolyline);
    }
    this.virtualWayPoints.push(filledCircle);
  }


  plotVirtualWayPoints(virtualWayPoints: any): void {
    if (!this.map) {
      throw new Error('Map not initialized. Call initialize() first.');
    }

    const bounds = new google.maps.LatLngBounds();
    this.clearVirtualWayPoints();

    Object.entries(virtualWayPoints || {}).forEach(([geofenceId, waypoints]) => {
      (waypoints as any[]).forEach((waypoint: {
        type: string,
        centerLng: number,
        centerLat: number,
        circleRadius: number
      }) => {
        if (waypoint.type === 'CIRCLE') {
          const lat = waypoint.centerLat;
          const lng = waypoint.centerLng;
          const radius = waypoint.circleRadius;

          this.createDashedCircle(lat, lng, radius);

          bounds.extend(new google.maps.LatLng(lat, lng));
        }
      });
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  clearGeofences(): void {
    if (this.geofencePoints) {
      this.geofencePoints.forEach((geofence) => geofence.setMap(null));
      this.geofencePoints = [];
    }
  }


  clearVirtualWayPoints(): void {
    if (this.virtualWayPoints) {
      this.virtualWayPoints.forEach((waypoint) => waypoint.setMap(null));
      this.virtualWayPoints = [];
    }
  }
}
